import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";

import {
  OrganizationProfile,
  putV1OrganizationsByOrganizationId,
  PutV1OrganizationsByOrganizationIdData,
  PutV1OrganizationsByOrganizationIdError,
  PutV1OrganizationsByOrganizationIdResponse,
} from "../../client";
import { jsonParse } from "../utils/parse";
import { getOrganizationOptions } from "./get-organization";
import { getOrganizationInfoOptions } from "./get-organization-info";

const mutationKey = ["updateOrganizationProfile"];

export const useUpdateOrganization = (
  options: UseMutationOptions<
    PutV1OrganizationsByOrganizationIdResponse,
    PutV1OrganizationsByOrganizationIdError,
    PutV1OrganizationsByOrganizationIdData
  > = {},
  organizationId: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey,
    mutationFn: async (data: PutV1OrganizationsByOrganizationIdData) => {
      const { data: responseData } = await putV1OrganizationsByOrganizationId({
        ...data,
        throwOnError: true,
      });
      return responseData;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      // Invalidate organization queries to refresh data
      queryClient.invalidateQueries({
        queryKey: getOrganizationOptions(organizationId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getOrganizationInfoOptions(organizationId).queryKey,
      });

      // Call the custom onSuccess handler if provided
      options?.onSuccess?.(data, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      // Call the custom onSettled handler if provided
      options?.onSettled?.(data, error, variables, context);
    },
  });
};
